/**
 * Toolbar rendering for DocTypeListPageShell.
 * Supports both compact (ClickUp-style) and standard layouts.
 * Includes search, filters, sort, density, view switcher, and report config.
 *
 * Extracted from DocTypeListPageShell.tsx for maintainability.
 */

import type { Table as TanstackTable } from "@tanstack/react-table";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, ListFilter, SlidersHorizontal } from "lucide-react";
import { DocTypeSearchAutocomplete } from "@/components/search/DocTypeSearchAutocomplete";
import { SortSelector } from "@/components/inventory/SortSelector";
import { DataTableToolbar } from "@/components/data-table/data-table-toolbar";
import { DataTableViewOptions } from "@/components/data-table/data-table-view-options";
import { ActiveFiltersDisplay } from "@/components/filter-builder/ActiveFiltersDisplay";
import { ListViewSwitcher } from "./ListViewSwitcher";
import { DensityDropdown } from "./DensityDropdown";
import { ListSettingsButton } from "./ListSettingsButton";
import { GroupBySelector, AggregationConfig, ReportColumnSelector } from "./report";
import type { FilterDescriptor } from "@/lib/erp";
import type { ListViewLayoutMode } from "@/lib/views/types";
import type { TableDensity, TableTextSize, ReportConfig } from "@/lib/store/preferencesStore";
import type { useMetaDrivenListSettings } from "@/lib/hooks/useMetaDrivenListSettings";
import type { DocTypeListPageShellProps } from "./DocTypeListPageShell.types";

export type ListToolbarProps<T> = {
  // Mode flags
  compactMode?: boolean;
  isFocusMode: boolean;
  isReportMode: boolean;
  isLoading: boolean;
  noPadding?: boolean;

  // View mode
  currentViewMode: ListViewLayoutMode;
  onViewModeChange: (mode: ListViewLayoutMode) => void;
  enableSimpleView?: boolean;
  enableViewModeSwitch?: boolean;
  enableReportView?: boolean;
  enableAdvancedView?: boolean;

  // Breadcrumbs (compact mode)
  breadcrumbs?: { label: string; href?: string; icon?: React.ReactNode }[];

  // Search
  doctype: string;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;
  searchConfig?: DocTypeListPageShellProps<T>['searchConfig'];
  onSearchSelect?: (value: string) => void;

  // Filters
  filters: FilterDescriptor[];
  onFiltersChange: (filters: FilterDescriptor[]) => void;
  onOpenFilterModal: () => void;
  onOpenAdvancedFilterModal: () => void;

  // Sort
  table: TanstackTable<T>;

  // Density
  tableDensity: TableDensity;
  setTableDensity: (d: TableDensity) => void;
  tableTextSize: TableTextSize;
  setTableTextSize: (s: TableTextSize) => void;

  // Settings
  listSettings: ReturnType<typeof useMetaDrivenListSettings> | null;
  onOpenColumnSidebar: () => void;

  // Report config
  reportConfig: ReportConfig;
  onReportConfigChange: (config: Partial<ReportConfig>) => void;

  // Header
  header?: React.ReactNode;
  headerBelow?: React.ReactNode;
  headerActions?: (
    table: TanstackTable<T>,
    context?: {
      listSettings?: {
        availableFields: import("@/lib/hooks/listSettingsTypes").ListSettingsFieldConfig[];
        visibleFieldOrder: string[];
      };
      filterStoreKey?: string;
    },
  ) => React.ReactNode;
  filterStoreKey?: string;
  title?: string;
  description?: string;
  newButtonLabel?: string;
  onNewClick?: () => void;

  // Translations
  translations: Record<string, string>;
};

export function ListToolbar<T>(props: ListToolbarProps<T>) {
  const {
    compactMode, isFocusMode, isReportMode, isLoading, noPadding,
    currentViewMode, onViewModeChange, enableSimpleView, enableViewModeSwitch, enableReportView, enableAdvancedView,
    breadcrumbs,
    doctype, searchQuery, onSearchChange, searchPlaceholder, searchConfig, onSearchSelect,
    filters, onFiltersChange, onOpenFilterModal, onOpenAdvancedFilterModal,
    table,
    tableDensity, setTableDensity, tableTextSize, setTableTextSize,
    listSettings, onOpenColumnSidebar,
    reportConfig, onReportConfigChange,
    header, headerBelow, headerActions, title, description, newButtonLabel, onNewClick,
    translations, filterStoreKey,
  } = props;

  // Build context for headerActions callback
  const headerActionsContext = {
    listSettings: listSettings ? {
      availableFields: listSettings.availableFields,
      visibleFieldOrder: listSettings.visibleFieldOrder,
    } : undefined,
    filterStoreKey,
  };

  if (compactMode) {
    return (
      <div
        aria-hidden={isFocusMode || undefined}
        // pt-2 below lg gives the first row breathing room under the mobile
        // sticky logo bar (ErpLayout). On desktop the toolbar sits flush at the
        // top of the scroll area (lg:pt-0) — the StandardListPage root's lg:mt-0
        // keeps it from being clipped, without adding a top gap.
        className={cn(noPadding && "px-4", "pt-2 lg:pt-0")}
        style={{
          maxHeight: isFocusMode ? 0 : 500,
          opacity: isFocusMode ? 0 : 1,
          overflow: 'hidden',
          pointerEvents: isFocusMode ? 'none' : undefined,
        }}
      >
        {/* Breadcrumb row */}
        {breadcrumbs && breadcrumbs.length > 0 && (
          <div className="flex items-center h-[50px] gap-1.5 text-sm text-muted-foreground">
            {breadcrumbs.map((crumb, i) => (
              <span key={i} className="flex items-center gap-1.5">
                {i > 0 && <span className="text-muted-foreground/40">/</span>}
                {crumb.href ? (
                  <a
                    href={crumb.href}
                    className="flex items-center gap-1.5 hover:text-foreground transition-colors"
                  >
                    {crumb.icon}
                    {crumb.label}
                  </a>
                ) : (
                  <span className={cn("flex items-center gap-1.5", i === breadcrumbs.length - 1 ? "text-foreground font-medium" : "")}>
                    {crumb.icon}
                    {crumb.label}
                  </span>
                )}
              </span>
            ))}
          </div>
        )}

        {/* Row 1: View tabs + New button */}
        <div className="flex items-center h-[50px] border-t border-border -mx-6 px-6">
          <div className="flex items-center gap-0.5">
            {(enableSimpleView || enableViewModeSwitch || enableReportView || enableAdvancedView) && (
              <ListViewSwitcher
                currentMode={currentViewMode}
                onModeChange={onViewModeChange}
                disabled={isLoading}
                enableSimpleView={enableSimpleView}
                enableReportView={enableReportView}
                enableAdvancedView={enableAdvancedView}
                variant="tabs"
                labels={{
                  simple: translations.viewSimple || "Simple",
                  table: translations.viewList || "List",
                  gallery: translations.viewBoard || "Board",
                  report: translations.viewTable || "Table",
                  advanced: translations.viewAdvanced || "Advanced",
                }}
              />
            )}
          </div>
          <div className="flex items-center gap-2 ml-auto">
            {headerActions && headerActions(table, headerActionsContext)}
            {onNewClick && newButtonLabel && (
              <Button
                onClick={onNewClick}
                size="sm"
                className="h-8 px-2.5 xl:px-3.5 text-sm font-medium shrink-0 shadow-none"
                style={{ borderRadius: 8 }}
                aria-label={newButtonLabel}
                title={newButtonLabel}
              >
                {/* This compact toolbar is used at ALL widths for this list. The
                    tight row (-mx-6 clip) overflows once the label is shown below
                    ~xl, so collapse to icon-only below xl and show the full label
                    at xl+ (desktop) where it fits comfortably. */}
                <Plus className="size-4 xl:-mr-0.5" />
                <span className="hidden xl:inline">{newButtonLabel}</span>
              </Button>
            )}
          </div>
        </div>

        {/* Row 2: Actions row — filters left, tools right */}
        <div className="flex items-center h-[55px] border-t border-border -mx-6 px-6">
          {/* Left group: Filter + Advanced filter with labels */}
          <div className="flex items-center gap-0.5">
            <button
              type="button"
              onClick={onOpenFilterModal}
              className={cn(
                "flex items-center gap-1.5 h-8 px-2.5 rounded-xs text-sm text-muted-foreground hover:text-foreground hover:bg-accent/60 transition-colors",
                filters.length > 0 && "text-foreground",
              )}
              aria-label={translations.addFilter || "Add filter"}
            >
              <ListFilter className="h-3.5 w-3.5" />
              <span>{translations.filter || "Filter"}</span>
            </button>

            <button
              type="button"
              onClick={onOpenAdvancedFilterModal}
              className={cn(
                "flex items-center gap-1.5 h-8 px-2.5 rounded-xs text-sm text-muted-foreground hover:text-foreground hover:bg-accent/60 transition-colors",
                filters.some(
                  (f) =>
                    f._metadata?.section === "assigned_to" ||
                    f._metadata?.section === "created_by" ||
                    f._metadata?.section === "tags"
                ) && "text-foreground",
              )}
              aria-label={translations.advancedFilters || "Advanced filters"}
            >
              <SlidersHorizontal className="h-3.5 w-3.5" />
              <span>{translations.advanced || "Advanced"}</span>
            </button>
          </div>

          {/* Right group: Search, Sort, Settings, New */}
          <div className="ml-auto flex items-center gap-0.5">
            {/* Search icon / inline search */}
            <div className="relative flex items-center">
              <DocTypeSearchAutocomplete
                doctype={doctype}
                value={searchQuery}
                onChange={(value) => onSearchChange(value)}
                onSelect={onSearchSelect}
                placeholder={searchPlaceholder || translations.searchPlaceholder}
                showInitialSuggestions={searchConfig?.showInitialSuggestions}
                minSearchLength={searchConfig?.minSearchLength}
                pageLength={searchConfig?.pageLength}
                filters={searchConfig?.filters}
                compact
              />
            </div>

            {/* Sort / Density / Settings — hidden in simple view (not applicable) */}
            {currentViewMode !== 'simple' && (
              <>
                {!isReportMode && (
                  <SortSelector table={table} translations={translations} compact />
                )}
                <DensityDropdown density={tableDensity} setDensity={setTableDensity} textSize={tableTextSize} setTextSize={setTableTextSize} />
                {listSettings && (
                  <ListSettingsButton
                    onClick={onOpenColumnSidebar}
                    compact
                  />
                )}
                {!listSettings && (
                  <DataTableViewOptions table={table} align="end" translations={translations} />
                )}
              </>
            )}

          </div>
        </div>

        {/* Row 2: Active filter pills (only when filters exist) */}
        {filters.length > 0 && (
          <div className="py-1.5 border-b border-border">
            <ActiveFiltersDisplay
              filters={filters}
              onRemoveFilter={(index) => {
                const newFilters = filters.filter((_, i) => i !== index);
                onFiltersChange(newFilters);
              }}
              onClearAll={() => onFiltersChange([])}
              translations={translations}
            />
          </div>
        )}

        {/* Report toolbar: GroupBy + Columns + Aggregations */}
        {isReportMode && (
          <div className="flex flex-wrap items-center gap-2 py-1.5 border-b border-border">
            <ReportColumnSelector
              doctype={doctype}
              value={reportConfig.visibleColumns}
              onChange={(columns) => onReportConfigChange({ visibleColumns: columns })}
            />
            <GroupBySelector
              doctype={doctype}
              value={reportConfig.groupByField}
              onChange={(field) => onReportConfigChange({ groupByField: field })}
            />
            <AggregationConfig
              doctype={doctype}
              value={reportConfig.aggregations}
              onChange={(aggregations) => onReportConfigChange({ aggregations })}
            />
          </div>
        )}

        {/* Content below header (e.g., quick filters bar) */}
        {headerBelow && <div>{headerBelow}</div>}
      </div>
    );
  }

  // Standard (non-compact) layout
  return (
    <>
      {/* Header chrome — collapses in focus mode */}
      <div
        aria-hidden={isFocusMode || undefined}
        style={{
          maxHeight: isFocusMode ? 0 : 500,
          opacity: isFocusMode ? 0 : 1,
          overflow: 'hidden',
          pointerEvents: isFocusMode ? 'none' : undefined,
          marginTop: isFocusMode ? 0 : undefined,
          marginBottom: isFocusMode ? 0 : undefined,
        }}
      >
        {/* Header */}
        {header || (
          <div className="flex items-center justify-between">
            <div>
              {title && <h1 className="text-3xl font-bold">{title}</h1>}
              {description && (
                <p className="text-muted-foreground mt-1">{description}</p>
              )}
            </div>
            <div className="flex items-center gap-3">
              {headerActions && headerActions(table, headerActionsContext)}
              {onNewClick && newButtonLabel && (
                <Button
                  onClick={onNewClick}
                  className="h-14 px-5 text-base font-normal rounded-full hover:scale-105 active:scale-95 transition-all duration-300 ease-out shrink-0"
                >
                  <Plus className="size-5 mr-2" />
                  {newButtonLabel}
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Content below header (e.g., quick filters bar) */}
        {headerBelow && <div>{headerBelow}</div>}
      </div>

      <div className="flex flex-col gap-2.5 overflow-hidden">
        {/* Filter/search chrome — collapses in focus mode */}
        <div
          aria-hidden={isFocusMode || undefined}
          className={cn("space-y-4", !isFocusMode && "py-4")}
          style={{
            maxHeight: isFocusMode ? 0 : 500,
            opacity: isFocusMode ? 0 : 1,
            overflow: isFocusMode ? 'hidden' : undefined,
            pointerEvents: isFocusMode ? 'none' : undefined,
            marginTop: isFocusMode ? 0 : undefined,
            marginBottom: isFocusMode ? 0 : undefined,
          }}
        >
          {/* Top row: View switcher + Settings */}
          <div className="flex items-center gap-2">
            {(enableSimpleView || enableViewModeSwitch || enableReportView || enableAdvancedView) && (
              <ListViewSwitcher
                currentMode={currentViewMode}
                onModeChange={onViewModeChange}
                disabled={isLoading}
                enableSimpleView={enableSimpleView}
                enableReportView={enableReportView}
                enableAdvancedView={enableAdvancedView}
              />
            )}
            <div className="ml-auto flex items-center gap-2">
              {listSettings && (
                <ListSettingsButton
                  onClick={onOpenColumnSidebar}
                />
              )}
              {!listSettings && (
                <DataTableViewOptions table={table} align="end" translations={translations} />
              )}
            </div>
          </div>

          {/* Search, Add Filter, Advanced Filters, Column Filters + Sort — all one row */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-[280px] max-w-[400px]">
              <DocTypeSearchAutocomplete
                doctype={doctype}
                value={searchQuery}
                onChange={(value) => onSearchChange(value)}
                onSelect={onSearchSelect}
                placeholder={searchPlaceholder || translations.searchPlaceholder}
                showInitialSuggestions={searchConfig?.showInitialSuggestions}
                minSearchLength={searchConfig?.minSearchLength}
                pageLength={searchConfig?.pageLength}
                filters={searchConfig?.filters}
              />
            </div>
            <Button
              onClick={onOpenFilterModal}
              variant="ghost"
              className="h-14 px-4 text-sm font-normal rounded-full bg-white dark:bg-gray-900 border border-border hover:scale-105 active:scale-95 transition-all duration-300 ease-out hover:bg-white/40 dark:hover:bg-gray-800/40 shrink-0"
              aria-label={translations.addFilter || "Add filter"}
            >
              <Plus className="size-4 mr-2" />
              {translations.addFilter || "Add Filter"}
            </Button>
            <Button
              onClick={onOpenAdvancedFilterModal}
              variant="ghost"
              className="h-14 px-4 text-sm font-normal rounded-full bg-white dark:bg-gray-900 border border-border hover:scale-105 active:scale-95 transition-all duration-300 ease-out hover:bg-white/40 dark:hover:bg-gray-800/40 shrink-0"
              aria-label={translations.advancedFilters || "Advanced filters"}
            >
              <Plus className="size-4 mr-2" />
              {translations.advancedFilters || "Advanced Filters"}
              {filters.some(
                (f) =>
                  f._metadata?.section === "assigned_to" ||
                  f._metadata?.section === "created_by" ||
                  f._metadata?.section === "tags"
              ) && (
                <Badge
                  variant="secondary"
                  className="ml-2 h-5 min-w-5 px-1.5 flex items-center justify-center rounded-full text-xs font-semibold"
                >
                  {
                    filters.filter(
                      (f) =>
                        f._metadata?.section === "assigned_to" ||
                        f._metadata?.section === "created_by" ||
                        f._metadata?.section === "tags"
                    ).length
                  }
                </Badge>
              )}
            </Button>
            {/* Column filters + Sort inline */}
            {!isReportMode && (
              <DataTableToolbar
                table={table}
                translations={translations}
                filters={filters}
                onFiltersChange={onFiltersChange}
                className="ml-auto w-auto flex-none"
              >
                <SortSelector table={table} translations={translations} />
              </DataTableToolbar>
            )}
          </div>

          {/* Active Filters Display */}
          {filters.length > 0 && (
            <ActiveFiltersDisplay
              filters={filters}
              onRemoveFilter={(index) => {
                const newFilters = filters.filter((_, i) => i !== index);
                onFiltersChange(newFilters);
              }}
              onClearAll={() => onFiltersChange([])}
              translations={translations}
            />
          )}

          {/* Report toolbar: GroupBy + Columns + Aggregations */}
          {isReportMode && (
            <div className="flex flex-wrap items-center gap-3 py-2">
              <ReportColumnSelector
                doctype={doctype}
                value={reportConfig.visibleColumns}
                onChange={(columns) => onReportConfigChange({ visibleColumns: columns })}
              />
              <GroupBySelector
                doctype={doctype}
                value={reportConfig.groupByField}
                onChange={(field) => onReportConfigChange({ groupByField: field })}
              />
              <AggregationConfig
                doctype={doctype}
                value={reportConfig.aggregations}
                onChange={(aggregations) => onReportConfigChange({ aggregations })}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
}
